<template>
  <div
    v-if="props.mode === 'hover'"
    class="floating-bar-activator"
    :class="[`edge-${props.edge}`]"
    @mouseenter="showHoverBar"
    @touchstart.passive="showHoverBarTemporarily"
  />
  <div
    ref="shellEl"
    class="floating-bar-shell"
    :class="[`edge-${props.edge}`, `mode-${props.mode}`]"
    :style="shellStyle"
    @mouseenter="showHoverBar"
    @mouseleave="hideHoverBar"
    @touchstart.passive="showHoverBarTemporarily"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import type { BarVisibility } from "@/stores/toolbar";

const props = defineProps<{
  edge: "top" | "bottom";
  mode: Extract<BarVisibility, "fixed" | "autohide" | "hover">;
  revealPx?: number;
}>();

const emit = defineEmits<{
  (e: "height-change", value: number): void;
}>();

const shellEl = ref<HTMLDivElement | null>(null);
const shellHeight = ref(0);
const hoverVisible = ref(false);
let hoverHideTimer: number | null = null;
let resizeObserver: ResizeObserver | null = null;

function clearHoverHideTimer() {
  if (hoverHideTimer !== null) {
    window.clearTimeout(hoverHideTimer);
    hoverHideTimer = null;
  }
}

function showHoverBar() {
  clearHoverHideTimer();
  hoverVisible.value = true;
}

function hideHoverBar() {
  if (props.mode !== "hover") return;
  clearHoverHideTimer();
  hoverHideTimer = window.setTimeout(() => {
    hoverVisible.value = false;
  }, 250);
}

function showHoverBarTemporarily() {
  if (props.mode !== "hover") return;
  showHoverBar();
  clearHoverHideTimer();
  hoverHideTimer = window.setTimeout(() => {
    hoverVisible.value = false;
  }, 2500);
}

const visiblePx = computed(() => {
  if (props.mode === "fixed") return shellHeight.value;
  if (props.mode === "hover") {
    return hoverVisible.value ? shellHeight.value : 0;
  }
  return Math.max(0, Math.min(props.revealPx ?? 0, shellHeight.value));
});

const shellStyle = computed(() => {
  const hiddenPx = Math.max(0, shellHeight.value - visiblePx.value);
  const translateY = props.edge === "top" ? -hiddenPx : hiddenPx;
  return {
    transform: `translateY(${translateY}px)`,
    opacity: visiblePx.value > 0 ? "1" : "0",
    pointerEvents: visiblePx.value > 0 ? "auto" : "none",
    paddingTop: props.edge === "top" ? "0.75rem" : "0",
    paddingBottom: props.edge === "bottom" ? "0.75rem" : "0",
  };
});

watch(
  () => shellEl.value,
  (element) => {
    resizeObserver?.disconnect();
    resizeObserver = null;

    if (element === null) {
      shellHeight.value = 0;
      emit("height-change", 0);
      return;
    }

    shellHeight.value = element.offsetHeight;
    emit("height-change", shellHeight.value);

    resizeObserver = new ResizeObserver(() => {
      shellHeight.value = element.offsetHeight;
      emit("height-change", shellHeight.value);
    });
    resizeObserver.observe(element);
  },
  { immediate: true },
);

watch(
  () => props.mode,
  (mode) => {
    if (mode !== "hover") {
      hoverVisible.value = false;
      clearHoverHideTimer();
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  clearHoverHideTimer();
  resizeObserver?.disconnect();
});
</script>

<style scoped lang="scss">
.floating-bar-activator,
.floating-bar-shell {
  position: fixed;
  left: env(safe-area-inset-left);
  right: env(safe-area-inset-right);
  z-index: 4;
  box-sizing: border-box;
}

.floating-bar-activator {
  height: 2rem;
  pointer-events: auto;

  &.edge-top {
    top: 0;
  }

  &.edge-bottom {
    bottom: 0;
  }
}

.floating-bar-shell {
  display: flex;
  justify-content: center;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  transition: transform 80ms linear, opacity 120ms ease;

  &.edge-top {
    top: env(safe-area-inset-top);
  }

  &.edge-bottom {
    bottom: env(safe-area-inset-bottom);
  }
}
</style>
